import type { Direction } from "../../domain/pips.ts";

/**
 * LO QUE HACE FALTA PARA REPETIR, y nada mas.
 *
 * Es un tipo ESTRUCTURAL y no `TradeDetail`: el boton vive en la tarjeta de la
 * bitacora Y en el detalle, y el listado del contrato no trae `accountId`. Pedir
 * el detalle completo obligaria a ofrecer el boton solo en una de las dos
 * pantallas, o a una peticion extra para copiar un campo opcional.
 *
 * `accountId` es opcional por eso: desde la bitacora no viaja, y la cuenta se
 * elige en el formulario como cualquier otro campo.
 */
export type Repeatable = {
  readonly instrumentId: string;
  readonly brokerId: string | null;
  readonly accountId?: string | null;
  readonly strategyId: string | null;
  readonly setupId: string | null;
  readonly direction: Direction;
  readonly entryTimeframe: string | null;
  readonly plannedRiskPips: number | null;
  readonly plannedRewardPips: number | null;
  readonly lotSize: number | null;
};

/**
 * REPETIR UNA OPERACIÓN: dejar el alta rellena con lo que no cambia.
 *
 * Lo pidió el propietario: «pon ese botón de Repetir y tomaría los datos ya
 * duplicados menos las capturas, y con eso me facilitaría solo cambiar el título
 * o algo diferente del trade».
 *
 * NO HAY ENDPOINT NI ESTADO NUEVO. El alta ya recupera un borrador de
 * `localStorage` en cada apertura (SPEC 15.5), así que repetir es escribir ese
 * mismo borrador y navegar. Una ruta `/trades/new?desde=<id>` habría necesitado
 * leer la operación otra vez y un camino aparte para rellenar el formulario;
 * esto reusa el que ya funciona y ya está probado.
 *
 * **LO QUE NO SE COPIA ES LA MITAD DE LA DECISIÓN**, y conviene leerlo:
 *
 *   - **Las capturas**, porque lo pidió así y porque son de ESA operación: el
 *     gráfico de ayer no explica la entrada de hoy.
 *   - **El resultado, el cierre, el MAE y el MFE.** Copiarlos daría una
 *     operación nueva que nace cerrada con el resultado de otra, y eso no es un
 *     atajo: es un dato falso en la estadística.
 *   - **La revisión entera** —emociones, adherencia, reglas rotas, lección—.
 *     Se escribe después de operar, sobre lo que pasó. Heredarla sería heredar
 *     la conclusión antes del hecho.
 *   - **El momento.** El alta pone la hora de ahora; repetir una operación de
 *     hace tres días con su hora la fecharía en el día equivocado.
 *   - **El título.** Es la tesis —el por qué, escrito antes de entrar— y el
 *     propietario dijo que quiere «solo cambiar el título»: dejarlo con el de la
 *     otra operación invita a guardarlo sin mirar, y entonces dos entradas
 *     distintas tienen la misma razón escrita.
 *
 * Lo que sí se copia es lo que de verdad se repite: símbolo, broker, cuenta,
 * dirección, estrategia, setup, temporalidad y el plan en pips —riesgo y
 * objetivo— más el lotaje.
 */

export const DRAFT_KEY = "draft:new-trade";

/**
 * Lo que `TradeModal` guarda en el borrador. Se declara como parcial a propósito:
 * el alta mezcla lo que encuentre sobre sus valores vacíos, así que escribir aquí
 * solo los campos que se repiten es exactamente lo que hace falta.
 */
export type RepeatDraft = {
  instrumentId: string;
  brokerId: string;
  accountId: string;
  strategyId: string;
  setupId: string;
  direction: string;
  timeframe: string;
  riskPips: string;
  rewardPips: string;
  lotSize: string;
};

/** Un número del detalle a texto para el formulario, o cadena vacía si no hay. */
function text(value: number | null | undefined): string {
  return value === null || value === undefined ? "" : String(value);
}

export function draftFromTrade(trade: Repeatable): RepeatDraft {
  return {
    instrumentId: trade.instrumentId,
    brokerId: trade.brokerId ?? "",
    accountId: trade.accountId ?? "",
    strategyId: trade.strategyId ?? "",
    setupId: trade.setupId ?? "",
    // La unica que no admite respaldo: el formulario arranca en "buy", asi que
    // una venta repetida naceria como compra si esto se perdiera.
    direction: trade.direction,
    timeframe: trade.entryTimeframe ?? "",
    riskPips: text(trade.plannedRiskPips),
    rewardPips: text(trade.plannedRewardPips),
    lotSize: text(trade.lotSize),
  };
}

/**
 * Deja el borrador listo y devuelve si se pudo.
 *
 * Si `localStorage` no está disponible —ventana privada, datos del sitio
 * bloqueados— se devuelve `false` y quien llama no navega: llevar al alta vacía
 * después de pulsar «Repetir» sería peor que no hacer nada, porque parecería que
 * el botón funcionó.
 */
export function holdRepeat(trade: Repeatable): boolean {
  try {
    window.localStorage.setItem(DRAFT_KEY, JSON.stringify(draftFromTrade(trade)));

    return true;
  } catch {
    return false;
  }
}
